import JobPercentageProgress from '../components/data-import/JobPercentageProgress';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDataManagement, type JobHistory } from '../hooks/useDataManagement';
import { useJobProgress, RunningJob } from '../hooks/useJobProgress';
import AddDataSourceModal from '../components/AddDataSourceModal';
import EditDataSourceModal from '../components/EditDataSourceModal';
import JobLogViewer from '../components/JobLogViewer';
import CalendarTriggerModal from '../components/CalendarTriggerModal';
import { HelpCircle, Activity, CheckCircle, Clock, Loader2, Database, RefreshCw, FileText, Play, Upload, Square } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import Chip from '../components/Chip';
import Button from '../components/Button';
import SearchField from '../components/SearchField';
import { 
  StandardTable, 
  StandardTableHeader, 
  StandardTableHeaderCell, 
  StandardTableBody, 
  StandardTableRow, 
  StandardTableCell 
} from '../components/StandardTable';
import api from '../lib/api';
import toast from 'react-hot-toast';
import DistrictMapsImportTab from '../components/data-import/DistrictMapsImportTab';
import SourceFreshness from '../components/data-import/SourceFreshness';
import OpenFecProgressSummary from '../components/data-import/OpenFecProgressSummary';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import Modal from '@shared/ui/Modal';
import { EditIcon } from '../components/icons';
import StatCard, { StatCardRow } from '../components/StatCard';

const FILE_UPLOAD_INIT_TIMEOUT_MS = 60_000;
const FILE_UPLOAD_TRANSFER_TIMEOUT_MS = 30 * 60_000;
const FILE_UPLOAD_COMPLETE_TIMEOUT_MS = 60_000;


function parseJobMetadata(metadata: unknown): Record<string, unknown> {
  if (metadata == null) return {};
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  if (typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata as Record<string, unknown>;
  }
  return {};
}

/** Prefer persisted record_count; for older jobs fall back to metadata from the importer. */
function jobHistoryDisplayRecordCount(job: { record_count?: number | null; metadata?: unknown }): number | null {
  if (job.record_count != null && job.record_count !== undefined) {
    return Number(job.record_count);
  }
  const meta = parseJobMetadata(job.metadata);
  const fromMeta =
    meta.row_count_source ?? meta.records_processed ?? meta.imported_records ?? meta.processed_records;
  if (fromMeta != null && Number.isFinite(Number(fromMeta))) {
    return Number(fromMeta);
  }
  return null;
}

interface DashboardTabProps {
  runningJobs: RunningJob[];
  pendingJobs: RunningJob[];
  jobProgress: Record<string | number, any>;
  activeTab: string;
  showAddModal: boolean;
  showEditModal: boolean;
  isImporting: boolean;
  importStatus: any;
  currentImportFile: any;
  showNewImportIndicator: boolean;
  stats: any;
  dataSources: any[];
  handleViewJobLogs: (jobId: string | number) => void;
  stoppingJobs: Set<string | number>;
  handleStopJob: (jobId: string | number, jobLabel: string) => Promise<void>;
}

const DashboardTab: React.FC<DashboardTabProps> = ({
  runningJobs,
  pendingJobs,
  jobProgress,
  activeTab,
  showAddModal,
  showEditModal,
  isImporting,
  importStatus,
  currentImportFile,
  showNewImportIndicator,
  stats,
  dataSources,
  handleViewJobLogs,
  stoppingJobs,
  handleStopJob
}) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h3 className="text-lg font-semibold">Dashboard Overview</h3>
      <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>

    {/* Running Jobs Section */}
    {runningJobs.length > 0 ? (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
          <h3 className="text-lg font-semibold">Running Jobs ({runningJobs.length})</h3>
          <div className="flex items-center space-x-1">
            {(() => {
              const anyModalOpen = showAddModal || showEditModal;

              if (anyModalOpen) {
                return (
                  <>
                    <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Monitoring paused (modal open)</span>
                  </>
                );
              } else if (activeTab !== 'dashboard') {
                return (
                  <>
                    <div className="w-2 h-2 bg-background-quaternary dark:bg-background-dark-quaternary rounded-full" />
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Monitoring paused (switch to Dashboard tab)</span>
                  </>
                );
              } else {
                return (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Live monitoring active</span>
                  </>
                );
              }
            })()}
          </div>
        </div>

        {runningJobs.map((job: RunningJob) => {
          const progress = jobProgress[job.id];
          return (
            <div key={job.id} className="card">
              {/* Job Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  <div>
                    <h4 className="font-semibold text-text-primary dark:text-text-dark-primary">{job.source_name}</h4>
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">{job.job_type}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {job.status}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Activity}
                    onClick={() => handleViewJobLogs(job.id)}
                    className="text-green-700 hover:bg-green-100 hover:text-green-800"
                  >
                    Logs
                  </Button>
                  {job.status === 'running' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={stoppingJobs.has(job.id) ? undefined : Square}
                      onClick={() => handleStopJob(job.id, job.source_name)}
                      disabled={stoppingJobs.has(job.id)}
                      loading={stoppingJobs.has(job.id)}
                      className="text-error-600 hover:text-error-800 dark:text-error-400 dark:hover:text-error-300"
                    >
                      Stop
                    </Button>
                  )}
                </div>
              </div>

              {/* Progress Details */}
              {progress?.progress?.openfec ? (
                <OpenFecProgressSummary progress={progress.progress.openfec} active={job.status === 'running'} />
              ) : progress ? (
                <div className="space-y-3">
                  <JobPercentageProgress stage={String(progress.progress.stage || 'Processing')} percentage={progress.progress.percentage} />

                  {/* Current Step */}
                  <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
                    <Activity className="w-4 h-4 mr-2" />
                    {String((progress.progress as any).current_step || (progress.progress as any).progress_message || 'Processing...')}
                  </div>

                  {/* Records Progress */}
                  {((Number((progress.progress as any).records_processed) || Number((progress.progress as any).processed_records) || 0) > 0) && (
                    <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
                      <Database className="w-4 h-4 mr-2" />
                      {(Number((progress.progress as any).records_processed) || Number((progress.progress as any).processed_records) || 0).toLocaleString()} processed
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                  <div className="flex items-center">
                    <Activity className="w-4 h-4 mr-2" />
                    Loading progress information...
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <div className="card">
        <div className="flex items-center space-x-3">
          <CheckCircle className="w-8 h-8 text-green-500" />
          <div>
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Running Jobs</h3>
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No imports are currently running</p>
          </div>
        </div>
      </div>
    )}

    {/* Queued Jobs Section */}
    {pendingJobs.length > 0 && (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Clock className="w-5 h-5 text-yellow-600" />
          <h3 className="text-lg font-semibold">Pending Jobs ({pendingJobs.length})</h3>
        </div>

        {pendingJobs.map((job: RunningJob, index: number) => (
          <div key={job.id} className="card border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 text-yellow-700 rounded-full font-semibold text-sm">
                  #{index + 1}
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary dark:text-text-dark-primary">{job.source_name}</h4>
                  <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">{job.job_type}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Pending
                </span>
                <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {new Date(job.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}

    {/* Stats Cards */}
    <StatCardRow>
      <StatCard
        label="Total Sources"
        value={stats?.totalSources || 0}
        variant="info"
        sublabel={`${dataSources?.filter(s => s.status === 'Active').length || 0} Active`}
      />
      <StatCard
        label="Successful Jobs"
        value={stats?.successfulJobs || 0}
        variant="success"
        sublabel="Last 24 hours"
      />
      <StatCard
        label="Failed Jobs"
        value={stats?.failedJobs || 0}
        variant={(stats?.failedJobs || 0) > 0 ? 'danger' : 'default'}
        sublabel={(stats?.failedJobs || 0) > 0 ? 'Needs attention' : 'All good'}
      />
      <StatCard
        label="Avg Duration"
        value={stats?.avgDuration || '0s'}
        variant="info"
        sublabel="Per job"
      />
    </StatCardRow>

    {/* Import Status */}
    {(isImporting || importStatus) && (
      <div
        id="import-progress-card"
        className={`card border-l-4 transition-all duration-300 ease-in-out ${importStatus?.errors && importStatus.errors.length > 0 ? 'border-red-500' : 'border-blue-500'
          }`}
        style={{
          animation: 'fadeInUp 0.5s ease-out'
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-lg font-semibold">
                {importStatus?.errors && importStatus.errors.length > 0
                  ? '❌ Bill Import Failed'
                  : isImporting
                    ? '🔄 Bill Import in Progress...'
                    : '✅ Bill Import Complete'}
              </h4>
              {showNewImportIndicator && (
                <Chip
                  label="NEW"
                  variant="info"
                  size="sm"
                  className="animate-pulse"
                />
              )}
            </div>
            {currentImportFile && (
              <div className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                <span className="font-medium">File:</span> {currentImportFile.name} •
                <span className="font-medium ml-2">Uploaded:</span> {new Date(currentImportFile.uploaded_at).toLocaleString()}
              </div>
            )}
          </div>
          {isImporting && !importStatus?.errors && (
            <LoadingSpinner size="md" />
          )}
        </div>

        {importStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium">Bills:</span> {importStatus.bills || 0}
            </div>
            <div>
              <span className="font-medium">Sponsors:</span> {importStatus.sponsors || 0}
            </div>
            <div>
              <span className="font-medium">Committees:</span> {importStatus.committees || 0}
            </div>
            <div>
              <span className="font-medium">Documents:</span> {importStatus.documents || 0}
            </div>
            <div>
              <span className="font-medium">Amendments:</span> {importStatus.amendments || 0}
            </div>
            <div>
              <span className="font-medium">History:</span> {importStatus.history || 0}
            </div>
            <div>
              <span className="font-medium">Votes:</span> {importStatus.chamberVotes || 0}
            </div>
            <div>
              <span className="font-medium">Errors:</span> {importStatus.errors?.length || 0}
            </div>
          </div>
        )}

        {importStatus?.errors && importStatus.errors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
            <h5 className="font-medium text-red-800 mb-2">Import Errors:</h5>
            <ul className="text-sm text-red-700 space-y-1">
              {importStatus.errors.slice(0, 5).map((error: any, index: number) => (
                <li key={index}>• {typeof error === 'string' ? error : `${error.bill || 'Unknown'}: ${error.error || error}`}</li>
              ))}
              {importStatus.errors.length > 5 && (
                <li className="text-red-600">... and {importStatus.errors.length - 5} more errors</li>
              )}
            </ul>
          </div>
        )}
      </div>
    )}
  </div>
);

interface SourcesTabProps {
  filteredDataSources: any[];
  dataSources: any[];
  typeFilter: string;
  statusFilter: string;
  searchFilter: string;
  setTypeFilter: React.Dispatch<React.SetStateAction<string>>;
  setStatusFilter: React.Dispatch<React.SetStateAction<string>>;
  setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
  clearFilters: () => void;
  isApiType: (type: string) => boolean;
  getJobStatusForSource: (sourceName: string) => { label: string; variant: 'success' | 'danger' | 'info' | 'warning' | 'neutral' } | null;
  handleTriggerJob: (sourceId: string, sourceName: string) => void;
  handleForceReimportJob?: (sourceId: string, sourceName: string) => void;
  handleForceFullReloadJob?: (sourceId: string, sourceName: string) => void;
  handleEditDataSource: (source: any) => void;
  handleViewLogs: (sourceId: string, sourceName: string) => void;
  handleAddDataSource: (data: any) => Promise<void>;
  handleUpdateDataSource: (id: number, data: any) => Promise<void>;
  handleViewJobLogs: (jobId: string | number) => void;
  showAddModal: boolean;
  setShowAddModal: React.Dispatch<React.SetStateAction<boolean>>;
  showEditModal: boolean;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  editingSource: any;
  showLogsModal: boolean;
  setShowLogsModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSourceLogs: any[];
  selectedSourceName: string;
}

const SourcesTab: React.FC<SourcesTabProps> = ({
  filteredDataSources,
  dataSources,
  typeFilter,
  statusFilter,
  searchFilter,
  setTypeFilter,
  setStatusFilter,
  setSearchFilter,
  clearFilters,
  isApiType,
  getJobStatusForSource,
  handleTriggerJob,
  handleForceReimportJob = undefined,
  handleForceFullReloadJob = undefined,
  handleEditDataSource,
  handleViewLogs,
  handleAddDataSource,
  handleUpdateDataSource,
  handleViewJobLogs,
  showAddModal,
  setShowAddModal,
  showEditModal,
  setShowEditModal,
  editingSource,
  showLogsModal,
  setShowLogsModal,
  selectedSourceLogs,
  selectedSourceName
}) => {
  // Per-source upload state: sourceId → { uploading, fileName }
  const [uploadState, setUploadState] = React.useState<Record<string, { uploading: boolean; fileName?: string }>>({});

  const getUploadConfig = (source: { type: string; import_type?: string }) => {
    if (source.import_type === 'voter_file') {
      return {
        accept: '.zip,application/zip,application/x-zip-compressed',
        buttonLabel: 'Upload ZIP',
      };
    }

    if (source.import_type === 'voting_locations') {
      return {
        accept: '.xlsx,.xls,.csv',
        buttonLabel: 'Upload Workbook',
      };
    }

    return {
      accept: '.csv',
      buttonLabel: 'Upload CSV',
    };
  };

  const handleFileUpload = async (sourceId: string, sourceName: string, file: File, importType?: string) => {
    setUploadState(prev => ({ ...prev, [sourceId]: { uploading: true, fileName: file.name } }));
    try {
      const isVoterSource = importType === 'voter_file';
      const uploadViaServer = async () => {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/data-management/sources/${sourceId}/upload-file`, fd, {
          timeout: FILE_UPLOAD_TRANSFER_TIMEOUT_MS,
        });
      };

      const initResponse = await api.post(`/data-management/sources/${sourceId}/upload-file/init`, {
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size: file.size,
      }, {
        timeout: FILE_UPLOAD_INIT_TIMEOUT_MS,
      });

      const initData = initResponse?.data?.data ?? {};
      let uploadModeLabel = 'server';
      if (initData.upload_mode === 'direct') {
        try {
          uploadModeLabel = 'direct';
          const uploadMethod = String(initData.method || 'PUT').toUpperCase();
          const uploadHeaders = { ...(initData.headers ?? {}) } as Record<string, string>;
          if (!uploadHeaders['Content-Type']) {
            uploadHeaders['Content-Type'] = file.type || 'application/octet-stream';
          }

          const putResponse = await fetch(String(initData.upload_url), {
            method: uploadMethod,
            headers: uploadHeaders,
            body: file,
          });
          if (!putResponse.ok) {
            throw new Error(`Direct upload failed with status ${putResponse.status}`);
          }

          await api.post(`/data-management/sources/${sourceId}/upload-file/complete`, {
            file_name: file.name,
            file_key: initData.file_key,
            content_type: file.type || 'application/octet-stream',
            size: file.size,
          }, {
            timeout: FILE_UPLOAD_COMPLETE_TIMEOUT_MS,
          });
        } catch (directUploadError) {
          if (isVoterSource) {
            throw directUploadError;
          }
          await uploadViaServer();
          uploadModeLabel = 'server-fallback';
        }
      } else {
        if (isVoterSource) {
          throw new Error(typeof initData.message === 'string'
            ? initData.message
            : 'Voter file uploads require direct storage upload in this environment.');
        }
        uploadModeLabel = 'server';
        await uploadViaServer();
      }

      toast.success(`"${file.name}" uploaded to ${sourceName} (${uploadModeLabel} upload). Click Trigger to start the import.`);
      setUploadState(prev => ({ ...prev, [sourceId]: { uploading: false, fileName: file.name } }));
    } catch (error) {
      const errMessage =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
          ? (error as { response: { data: { error: string } } }).response.data.error
          : error instanceof Error
            ? error.message
            : 'Upload failed';
      toast.error(`Failed to upload file to ${sourceName}: ${errMessage}`);
      setUploadState(prev => ({ ...prev, [sourceId]: { uploading: false } }));
    }
  };

  return (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-semibold">Data Sources</h3>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
          Showing {filteredDataSources?.length || 0} of {dataSources?.length || 0} sources
          {(typeFilter || statusFilter || searchFilter) && (
            <button
              onClick={clearFilters}
              className="ml-2 text-blue-600 hover:text-blue-800 underline"
            >
              Clear filters
            </button>
          )}
        </p>
      </div>
    </div>

    {/* Filters */}
    <div className="flex flex-wrap gap-4 items-center">
      <select
        className="input text-sm"
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
      >
        <option value="">All Types</option>
        <optgroup label="File-based Sources">
          <option value="CSV Upload">CSV Upload</option>
          <option value="Web Scraper">Web Scraper</option>
        </optgroup>
        <optgroup label="API Sources">
          <option value="Government API">Government API</option>
        </optgroup>
      </select>
      <select
        className="input text-sm"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="">All Statuses</option>
        <option value="Active">Active</option>
        <option value="Paused">Paused</option>
        <option value="Error">Error</option>
      </select>
      <SearchField
        value={searchFilter}
        onChange={(value) => setSearchFilter(value)}
        placeholder="Search sources by name or description..."
        size="sm"
      />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredDataSources?.map((source) => (
        <div key={source.id} className="card flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">{source.name}</h4>
                  {isApiType(source.type) && (
                    <span title="API Data Source">
                      <Activity className="w-4 h-4 text-blue-500" />
                    </span>
                  )}
                </div>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">{source.description}</p>
                {/* Special note for charitable organizations data sources */}
                {(source.name.toLowerCase().includes('charitable') ||
                  source.name.toLowerCase().includes('charity') ||
                  source.name.toLowerCase().includes('charities') ||
                  source.name.toLowerCase().includes('nonprofit') ||
                  source.name.toLowerCase().includes('non-profit') ||
                  source.description?.toLowerCase().includes('charitable') ||
                  source.description?.toLowerCase().includes('charity') ||
                  source.description?.toLowerCase().includes('charities') ||
                  source.description?.toLowerCase().includes('nonprofit') ||
                  source.description?.toLowerCase().includes('non-profit')) ? (
                    <div className="mt-2 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
                      💡 Will display as Non-Profit Organizations in the system
                    </div>
                  ) : (
                    <div className="mt-2 px-2 py-1"></div>
                  )}
              </div>
              <div className="flex flex-col gap-1">
                <Chip
                  className="justify-center whitespace-nowrap"
                  label={
                    source.status === 'Active' ? '✅ Active' :
                      source.status === 'Paused' ? '⏸ Paused' :
                        source.status === 'Error' ? '❌ Error' :
                          source.status
                  }
                  variant={
                    source.status === 'Active' ? 'success' :
                      source.status === 'Paused' ? 'warning' :
                        source.status === 'Error' ? 'danger' :
                          'neutral'
                  }
                  size="sm"
                />
                {(() => {
                  const jobStatus = getJobStatusForSource(source.name);
                  if (jobStatus) {
                    return (
                      <Chip
                        label={jobStatus.label}
                        variant={jobStatus.variant}
                        className="justify-center"
                        size="sm"
                      />
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary dark:text-text-dark-tertiary">Type:</span>
                <span className="font-medium">{source.type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary dark:text-text-dark-tertiary">Frequency:</span>
                <span className="font-medium">{source.schedule || 'Manual'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary dark:text-text-dark-tertiary">Last Sync:</span>
                <span className="font-medium">{source.last_load_time ?  new Date(source.last_load_time).toLocaleString() : 'Never'}</span>
              </div>
              <SourceFreshness status={source.status} schedule={source.schedule} lastLoadTime={source.last_load_time} />
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary dark:text-text-dark-tertiary">Records:</span>
                <span className="font-medium">{source.record_count || 0}</span>
              </div>
            </div>
          </div>

          {/* Upload button — file-based sources with admin-upload workflow */}
          {source.type === 'CSV Upload' && (
            <div className="mb-3">
              {(() => {
                const uploadConfig = getUploadConfig(source);
                return (
              <label className="w-full cursor-pointer">
                <input
                  type="file"
                  accept={uploadConfig.accept}
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(source.id.toString(), source.name, file, source.import_type);
                    e.target.value = '';
                  }}
                />
                <span className={`flex items-center justify-center gap-2 w-full px-3 py-1.5 rounded-md text-sm font-medium border transition-colors
                  ${uploadState[source.id]?.uploading
                    ? 'bg-border-light dark:bg-border-dark text-text-tertiary dark:text-text-dark-tertiary cursor-not-allowed'
                    : 'border-border-light dark:border-border-dark text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                  }`}>
                  {uploadState[source.id]?.uploading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                    : <><Upload className="w-3.5 h-3.5" /> {uploadConfig.buttonLabel}</>
                  }
                </span>
              </label>
                );
              })()}
              {uploadState[source.id]?.fileName && !uploadState[source.id]?.uploading && (
                <p className="text-xs text-success-600 dark:text-success-400 mt-1 truncate">
                  Ready: {uploadState[source.id].fileName}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 2xl:grid-cols-3 gap-2 mb-4">
            <Button
              variant="primary"
              size="sm"
              icon={Play}
              onClick={() => handleTriggerJob(source.id.toString(), source.name)}
              className="flex items-center justify-center"
              ariaLabel="Trigger manual data sync with progress monitoring"
            >
              Trigger
            </Button>

            {source.import_type === 'bill' && handleForceReimportJob && (
              <Button
                variant="outline"
                size="sm"
                icon={RefreshCw}
                onClick={() => handleForceReimportJob(source.id.toString(), source.name)}
                className="flex items-center justify-center"
                ariaLabel="Force reimport (bypass already-processed cache)"
              >
                Force Reimport
              </Button>
            )}

            {source.import_type === 'campaign_finance' && handleForceFullReloadJob && (
              <Button
                variant="outline"
                size="sm"
                icon={RefreshCw}
                onClick={() => handleForceFullReloadJob(source.id.toString(), source.name)}
                className="flex items-center justify-center"
                ariaLabel="Force full reload (truncate and re-import every year)"
              >
                Force Full Reload
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              icon={EditIcon}
              onClick={() => handleEditDataSource(source)}
              className="flex items-center justify-center"
              ariaLabel="Edit data source configuration"
            >
              Edit
            </Button>

            <Button
              variant="secondary"
              size="sm"
              icon={Activity}
              onClick={() => handleViewLogs(source.id.toString(), source.name)}
              className="flex items-center justify-center"
              ariaLabel="View logs for this data source"
            >
              View Logs
            </Button>
          </div>

        </div>
      ))}

      {/* Empty state when no sources match filters */}
      {filteredDataSources?.length === 0 && dataSources?.length > 0 && (
        <div className="col-span-full text-center py-12">
          <div className="text-text-tertiary dark:text-text-dark-tertiary">
            <Database className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No sources match your filters</h3>
            <p className="text-sm mb-4">
              Try adjusting your filter criteria or
              <button
                onClick={clearFilters}
                className="ml-1 text-blue-600 hover:text-blue-800 underline"
              >
                clear all filters
              </button>
            </p>
          </div>
        </div>
      )}
    </div>

    {showAddModal && (
      <AddDataSourceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddDataSource}
      />
    )}

    {/* Edit Data Source Modal */}
    {showEditModal && (
      <EditDataSourceModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateDataSource}
        dataSource={editingSource}
      />
    )}

    {/* Logs Modal */}
    {showLogsModal && (
      <Modal
        isOpen={true}
        title={`Job logs — ${selectedSourceName}`}
        onClose={() => setShowLogsModal(false)}
        size="xl"
        footer={
          <Button variant="secondary" onClick={() => setShowLogsModal(false)}>
            Close
          </Button>
        }
      >
            {selectedSourceLogs.length === 0 ? (
              <p className="text-text-tertiary dark:text-text-dark-tertiary text-center py-8">No job history found for this data source.</p>
            ) : (
              <div className="space-y-3">
                {selectedSourceLogs.map((log) => (
                  <div key={log.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{log.job_type}</span>
                          <Chip
                            label={log.status}
                            variant={
                              log.status === 'completed' ? 'success' :
                                log.status === 'failed' ? 'danger' :
                                  log.status === 'running' ? 'info' :
                                    'neutral'
                            }
                            size="sm"
                          />
                        </div>
                        <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          Job ID: {log.id}
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setShowLogsModal(false);
                          handleViewJobLogs(log.id);
                        }}
                        variant="outline"
              size="sm"
                        title="View detailed job logs"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-2">
                      <div>
                        <span className="text-text-tertiary dark:text-text-dark-tertiary">Start Time:</span>
                        <span className="ml-1 font-medium block text-xs">
                          {new Date(log.start_time).toLocaleString()}
                        </span>
                      </div>
                      {log.end_time ? (
                        <div>
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">End Time:</span>
                          <span className="ml-1 font-medium block text-xs">
                            {new Date(log.end_time).toLocaleString()}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">End Time:</span>
                          <span className="ml-1 font-medium">N/A</span>
                        </div>
                      )}
                      {log.duration_seconds ? (
                        <div>
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">Duration:</span>
                          <span className="ml-1 font-medium">{log.duration_seconds}s</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">Duration:</span>
                          <span className="ml-1 font-medium">N/A</span>
                        </div>
                      )}
                      {log.record_count ? (
                        <div>
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">Records:</span>
                          <span className="ml-1 font-medium">{log.record_count}</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-text-tertiary dark:text-text-dark-tertiary">Records:</span>
                          <span className="ml-1 font-medium">N/A</span>
                        </div>
                      )}
                    </div>

                    {log.error_summary && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                        <span className="text-sm font-medium text-red-800 dark:text-red-300">Error:</span>
                        <span className="text-sm text-red-700 dark:text-red-400 ml-1">{log.error_summary}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
      </Modal>
    )}
  </div>
  );
};

interface HistoryTabProps {
  jobHistory: JobHistory[];
  retryingJobs: Set<string | number>;
  stoppingJobs: Set<string | number>;
  handleViewJobLogs: (jobId: string | number) => void;
  handleRetryFailedJob: (sourceId: number, jobId: string | number, sourceName: string) => Promise<void>;
  handleStopJob: (jobId: string | number, jobLabel: string) => Promise<void>;
}

const HistoryTab: React.FC<HistoryTabProps> = ({
  jobHistory,
  retryingJobs,
  stoppingJobs,
  handleViewJobLogs,
  handleRetryFailedJob,
  handleStopJob
}) => (
  <div className="space-y-6">
    <h3 className="text-lg font-semibold">Job History</h3>

    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-border-light dark:border-border-dark">
        <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Recent Jobs</h4>
      </div>
      <StandardTable>
        <StandardTableHeader>
          <tr>
            <StandardTableHeaderCell columnType="auto">
              Job ID
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="auto">
              Source
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">
              Type
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">
              Status
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">
              Records
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">
              Duration
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">
              Errors
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="fit">
              Created
            </StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="actions">
              Actions
            </StandardTableHeaderCell>
          </tr>
        </StandardTableHeader>
        <StandardTableBody>
          {jobHistory?.map((job) => {
            const hasRetry = jobHistory?.some((j) => {
              const retryOf = parseJobMetadata(j.metadata).retry_of;
              return retryOf != null && String(retryOf) === String(job.id);
            });

            return (
            <StandardTableRow key={job.id}>
              <StandardTableCell>
                <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  #{job.id}
                </span>
              </StandardTableCell>
              <StandardTableCell>
                <span className="text-sm text-text-primary dark:text-text-dark-primary">
                  {job.source_name}
                </span>
                {(() => {
                  const fn = parseJobMetadata(job.metadata).file_name;
                  if (typeof fn !== 'string' || !fn.trim()) return null;
                  return (
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5 truncate max-w-xs" title={fn}>
                      {fn}
                    </div>
                  );
                })()}
              </StandardTableCell>
              <StandardTableCell>
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {job.job_type}
                </span>
              </StandardTableCell>
              <StandardTableCell>
                <Chip
                  label={job.status}
                  variant={
                    job.status === 'completed' ? 'success' :
                      job.status === 'failed' ? 'danger' :
                        job.status === 'running' ? 'info' :
                          job.status === 'cancelled' ? 'warning' :
                            'neutral'
                  }
                  size="sm"
                />
              </StandardTableCell>
              <StandardTableCell>
                {(() => {
                  const n = jobHistoryDisplayRecordCount(job);
                  if (n !== null) {
                    return (
                  <span className={`text-sm font-medium ${job.status === 'completed' ? 'text-success-600 dark:text-success-400' :
                    job.status === 'failed' ? 'text-error-600 dark:text-error-400' :
                      'text-text-primary dark:text-text-dark-primary'
                    }`}>
                    {n.toLocaleString()}
                  </span>
                    );
                  }
                  return (
                  <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
                  );
                })()}
              </StandardTableCell>
              <StandardTableCell>
                {job.duration_seconds !== null && job.duration_seconds !== undefined ? (
                  <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                    {job.duration_seconds < 60
                      ? `${job.duration_seconds}s`
                      : job.duration_seconds < 3600
                        ? `${Math.floor(job.duration_seconds / 60)}m ${job.duration_seconds % 60}s`
                        : `${Math.floor(job.duration_seconds / 3600)}h ${Math.floor((job.duration_seconds % 3600) / 60)}m`
                    }
                  </span>
                ) : (
                  <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
                )}
              </StandardTableCell>
              <StandardTableCell>
                {job.error_summary ? (
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200">
                      Error
                    </span>
                    <span className="text-error-600 dark:text-error-400 text-xs truncate max-w-24" title={job.error_summary}>
                      {job.error_summary.length > 20 ? `${job.error_summary.substring(0, 20)}...` : job.error_summary}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>
                )}
              </StandardTableCell>
              <StandardTableCell>
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {new Date(job.created_at).toLocaleString()}
                </span>
              </StandardTableCell>
              <StandardTableCell>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewJobLogs(job.id)}
                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    View Logs
                  </Button>
                  {job.status === 'running' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={stoppingJobs.has(job.id) ? undefined : Square}
                      onClick={() => handleStopJob(job.id, job.source_name)}
                      disabled={stoppingJobs.has(job.id)}
                      loading={stoppingJobs.has(job.id)}
                      className="text-error-600 hover:text-error-800 dark:text-error-400 dark:hover:text-error-300"
                    >
                      Stop
                    </Button>
                  )}
                  {job.status === 'failed' && !hasRetry && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={retryingJobs.has(job.id) ? undefined : RefreshCw}
                      onClick={() => handleRetryFailedJob(job.source_id, job.id, job.source_name)}
                      disabled={retryingJobs.has(job.id)}
                      loading={retryingJobs.has(job.id)}
                      className="text-warning-600 hover:text-warning-800 dark:text-warning-400 dark:hover:text-warning-300"
                    >
                      Retry
                    </Button>
                  )}
                </div>
                </StandardTableCell>
              </StandardTableRow>
            )})}
        </StandardTableBody>
      </StandardTable>
    </div>
  </div>
);

const DataImportPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get('tab') === 'district-maps' ? 'district-maps' : 'dashboard'
  );

  const selectTab = useCallback(
    (id: string) => {
      setActiveTab(id);
      if (id === 'district-maps') {
        setSearchParams({ tab: 'district-maps' });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    const isDistrictMapsUrl = searchParams.get('tab') === 'district-maps';
    if (isDistrictMapsUrl) {
      setActiveTab('district-maps');
      return;
    }
    setActiveTab((prev) => (prev === 'district-maps' ? 'dashboard' : prev));
  }, [searchParams]);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedSourceLogs, setSelectedSourceLogs] = useState<any[]>([]);
  const [selectedSourceName, setSelectedSourceName] = useState('');
  const [selectedJobIdForLogs, setSelectedJobIdForLogs] = useState<string | number | null>(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSource, setEditingSource] = useState<any>(null);
  
  // Calendar trigger modal state
  const [showCalendarTriggerModal, setShowCalendarTriggerModal] = useState(false);
  const [calendarSourceInfo, setCalendarSourceInfo] = useState<{ id: string; name: string } | null>(null);

  // Job progress monitoring
  const { runningJobs, pendingJobs, jobProgress, startMonitoring, stopMonitoring, stopJob } = useJobProgress(5000);
  const [retryingJobs, setRetryingJobs] = useState<Set<string | number>>(new Set());
  const [stoppingJobs, setStoppingJobs] = useState<Set<string | number>>(new Set());

  // Import status state
  const [isImporting] = useState(false);
  const [importStatus] = useState<any>(null);
  const [currentImportFile] = useState<any>(null);
  const [showNewImportIndicator] = useState(false);

  // Filter state
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const {
    dataSources,
    jobHistory,
    stats,
    isLoading,
    error,
    createDataSource,
    updateDataSource,
    triggerDataSourceJob,
    getDataSourceLogs,
    refetchDataSources,
    refetchJobHistory,
    refetchAll,
  } = useDataManagement();

  // Filter data sources based on current filter state
  const filteredDataSources = useMemo(() => {
    return dataSources?.filter(source => {
      if (typeFilter && source.type !== typeFilter) {
        return false;
      }

      if (statusFilter && source.status !== statusFilter) {
        return false;
      }

      if (searchFilter) {
        const searchLower = searchFilter.toLowerCase();
        const nameMatch = source.name.toLowerCase().includes(searchLower);
        const descriptionMatch = source.description?.toLowerCase().includes(searchLower) || false;
        if (!nameMatch && !descriptionMatch) {
          return false;
        }
      }

      return true;
    });
  }, [dataSources, typeFilter, statusFilter, searchFilter]);

  const clearFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setSearchFilter('');
  };

  // Only monitor jobs when on Dashboard tab (allow monitoring even with logs modal open)
  useEffect(() => {
    const criticalModalOpen = showAddModal || showEditModal;
    const shouldMonitor = activeTab === 'dashboard' && !criticalModalOpen;


    if (shouldMonitor) {
      // Start monitoring only when on Dashboard tab and no modals are open
      startMonitoring();
    } else {
      // Stop monitoring when not on Dashboard tab or when modals are open
      stopMonitoring();
    }

    return () => stopMonitoring();
  }, [startMonitoring, stopMonitoring, activeTab, showAddModal, showEditModal, showLogsModal]);

  // Poll job history when on History tab (3s interval)
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refetchJobHistory();
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeTab, refetchJobHistory]);

  const handleTriggerJob = async (sourceId: string, sourceName: string) => {
    // Check if this is the calendar data source - show special modal with options
    if (sourceName.toLowerCase().includes('calendar')) {
      setCalendarSourceInfo({ id: sourceId, name: sourceName });
      setShowCalendarTriggerModal(true);
      return;
    }
    
    // For other sources, trigger directly
    await triggerJobWithOptions(sourceId, sourceName);
  };

  const triggerJobWithOptions = async (
    sourceId: string, 
    sourceName: string, 
    options?: { targetDate?: string; forceRefresh?: boolean; forceReimport?: boolean; forceBackfill?: boolean }
  ) => {
    try {
      const result = await triggerDataSourceJob(sourceId, options);

      if (result.status === 'pending') {
        alert(`🔄 Job Queued for ${sourceName}\n\nPosition in queue: ${result.queue_position || 'Unknown'}\nThe job will start automatically when the current job completes.`);
      } else {
        alert(`✅ Job Started for ${sourceName}\n\n${result.message}`);
      }

      // Refresh key data to reflect new job state
      await refetchAll();
      setActiveTab('dashboard');
    } catch (error: any) {

      // Handle queue conflicts with user-friendly messages
      if (error.isQueueConflict) {
        const runningJob = error.runningJob;
        const startTime = runningJob ? new Date(runningJob.created_at).toLocaleTimeString() : 'Unknown';

        alert(`⚠️ Job Already Running for ${sourceName}\n\n` +
          `A ${runningJob?.job_type || 'job'} started at ${startTime} is still running.\n\n` +
          `${error.message}\n\n` +
          `You can monitor the progress below or wait for it to complete.`);
      } else {
        alert(`❌ Failed to trigger job for ${sourceName}\n\n${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleCalendarTrigger = (options: { targetDate: string; forceRefresh: boolean }) => {
    if (calendarSourceInfo) {
      setShowCalendarTriggerModal(false);
      triggerJobWithOptions(calendarSourceInfo.id, calendarSourceInfo.name, options);
    }
  };

  const handleForceReimportJob = async (sourceId: string, sourceName: string) => {
    await triggerJobWithOptions(sourceId, sourceName, { forceReimport: true });
  };

  // Force a clean, full historical reload: TRUNCATEs the campaign-finance tables and
  // re-imports every year from the source. Destructive, so confirm first.
  const handleForceFullReloadJob = async (sourceId: string, sourceName: string) => {
    const confirmed = window.confirm(
      `Force a FULL RELOAD of "${sourceName}"?\n\n` +
      `This EMPTIES the campaign finance contributions, expenditures, and loan tables, ` +
      `then re-imports every year from the source (TRACER). The data is briefly ` +
      `incomplete while the reload runs.\n\n` +
      `Use this for a clean rebuild — e.g. after an ingestion fix. Run it when traffic is low.`
    );
    if (!confirmed) return;
    await triggerJobWithOptions(sourceId, sourceName, { forceBackfill: true });
  };

  const isApiType = (type: string) => {
    return type.toLowerCase().includes('api');
  };

  const getJobStatusForSource = (sourceName: string): { label: string; variant: 'success' | 'danger' | 'info' | 'warning' | 'neutral' } | null => {
    // Check for running job first
    const runningJob = runningJobs.find((job: RunningJob) => job.source_name === sourceName);
    if (runningJob) {
      return { label: 'Running', variant: 'info' };
    }

    // If no running job, find most recent completed job from history
    const sourceJobs = jobHistory?.filter((job) => job.source_name === sourceName);
    if (!sourceJobs || sourceJobs.length === 0) {
      return null; // No job history for this source
    }

    const mostRecentJob = sourceJobs[0];

    switch (mostRecentJob.status) {
      case 'completed':
        return { label: 'Completed', variant: 'success' };
      case 'failed':
        return { label: 'Failed', variant: 'danger' };
      case 'cancelled':
        return { label: 'Cancelled', variant: 'warning' };
      case 'running':
        return { label: 'Running', variant: 'info' };
      default:
        return { label: `${mostRecentJob.status}`, variant: 'neutral' };
    }
  };

  const handleViewLogs = async (sourceId: string, sourceName: string) => {
    try {
      const logs = await getDataSourceLogs(sourceId, 20);
      setSelectedSourceLogs(logs.logs);
      setSelectedSourceName(sourceName);
      setShowLogsModal(true);
    } catch (error) {
      alert('Failed to fetch logs. Please try again.');
    }
  };

  const handleViewJobLogs = (jobId: string | number) => {
    setSelectedJobIdForLogs(jobId);
  };

  const handleAddDataSource = async (data: any) => {
    try {
      await createDataSource(data);
      await refetchDataSources();
    } catch (error) {
      // You could add a toast notification here
    }
  };

  // Edit data source handlers
  const handleEditDataSource = (source: any) => {
    setEditingSource(source);
    setShowEditModal(true);
  };

  const handleUpdateDataSource = async (id: number, data: any) => {
    try {
      await updateDataSource(id, data);
      await refetchDataSources();
      setShowEditModal(false);
      setEditingSource(null);
    } catch (error) {
      alert('Failed to update data source. Please try again.');
    }
  };

  const handleStopJob = async (jobId: string | number, jobLabel: string) => {
    if (stoppingJobs.has(jobId)) return;

    const confirmStop = window.confirm(
      `Stop job for "${jobLabel}"?\n\nThe import will be cancelled. For large voter file imports, use Retry on a failed job to resume from staging instead of Trigger.`
    );
    if (!confirmStop) return;

    setStoppingJobs(prev => new Set([...prev, jobId]));

    try {
      toast.loading('Stopping job...', { id: 'stop-job' });
      await stopJob(jobId);
      toast.success('Job stopped', { id: 'stop-job' });
      await refetchJobHistory();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to stop job', { id: 'stop-job' });
    } finally {
      setStoppingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  // Retry failed job handler
  const handleRetryFailedJob = async (sourceId: number, jobId: string | number, sourceName: string) => {
    if (retryingJobs.has(jobId)) return; // Prevent double-clicking

    const confirmRetry = window.confirm(`Retry failed job for "${sourceName}"?\n\nThis will create a new job that attempts to re-run the failed operation.`);
    if (!confirmRetry) return;

    setRetryingJobs(prev => new Set([...prev, jobId]));

    try {
      toast.loading('Starting retry...', { id: 'retry-job' });

      await api.post(`/data-management/sources/${sourceId}/retry`);

      toast.success('✅ Retry job started successfully!', { id: 'retry-job' });
      await refetchJobHistory();
    } catch (error: any) {
      toast.error(`❌ Unable to retry the job. Please try again and ensure that no identical job is currently running.`, { id: 'retry-job' });
    } finally {
      setRetryingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'sources', label: 'Data Sources', icon: '🔗' },
    { id: 'history', label: 'Job History', icon: '📋' },
    { id: 'district-maps', label: 'District maps', icon: '🗺️' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pt-8">
        <div className="flex justify-between items-center">
          <div className="flex items-start gap-4">
            <div>
              <h1 className="text-3xl font-bold">Data Import Management</h1>
              <p className="text-muted-foreground">
                Manage data sources, monitor import jobs, and configure validation rules
              </p>
            </div>
          </div>
          <Link
            to="/admin/data-import/help"
            className="inline-flex items-center justify-center w-10 h-10 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            title="View help and instructions for Data Import Management"
            aria-label="View help"
          >
            <HelpCircle className="w-5 h-5" />
          </Link>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-8">
      <div className="flex justify-between items-center">
        <div className="flex items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold">Data Import Management</h1>
            <p className="text-muted-foreground">
              Manage data sources, monitor import jobs, and configure validation rules
            </p>
          </div>
        </div>
        <Link
          to="/admin/data-import/help"
          className="inline-flex items-center justify-center w-10 h-10 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          title="View help and instructions for Data Import Management"
          aria-label="View help"
        >
          <HelpCircle className="w-5 h-5" />
        </Link>
      </div>

      {/* Tabs */}
      <div className="card">
        <nav className="flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={`flex items-center px-4 py-3 rounded-2xl font-medium text-sm transition-all duration-200 ${activeTab === tab.id
                ? 'bg-primary-700 text-white shadow-md'
                : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
                }`}
            >
              <span className="mr-2 text-lg">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {activeTab === 'dashboard' && (
          <DashboardTab
            runningJobs={runningJobs}
            pendingJobs={pendingJobs}
            jobProgress={jobProgress}
            activeTab={activeTab}
            showAddModal={showAddModal}
            showEditModal={showEditModal}
            isImporting={isImporting}
            importStatus={importStatus}
            currentImportFile={currentImportFile}
            showNewImportIndicator={showNewImportIndicator}
            stats={stats}
            dataSources={dataSources}
            handleViewJobLogs={handleViewJobLogs}
            stoppingJobs={stoppingJobs}
            handleStopJob={handleStopJob}
          />
        )}
        {activeTab === 'sources' && (
          <SourcesTab
            filteredDataSources={filteredDataSources || []}
            dataSources={dataSources || []}
            typeFilter={typeFilter}
            statusFilter={statusFilter}
            searchFilter={searchFilter}
            setTypeFilter={setTypeFilter}
            setStatusFilter={setStatusFilter}
            setSearchFilter={setSearchFilter}
            clearFilters={clearFilters}
            isApiType={isApiType}
            getJobStatusForSource={getJobStatusForSource}
            handleTriggerJob={handleTriggerJob}
            handleForceReimportJob={handleForceReimportJob}
            handleForceFullReloadJob={handleForceFullReloadJob}
            handleEditDataSource={handleEditDataSource}
            handleViewLogs={handleViewLogs}
            handleAddDataSource={handleAddDataSource}
            handleUpdateDataSource={handleUpdateDataSource}
            handleViewJobLogs={handleViewJobLogs}
            showAddModal={showAddModal}
            setShowAddModal={setShowAddModal}
            showEditModal={showEditModal}
            setShowEditModal={setShowEditModal}
            editingSource={editingSource}
            showLogsModal={showLogsModal}
            setShowLogsModal={setShowLogsModal}
            selectedSourceLogs={selectedSourceLogs}
            selectedSourceName={selectedSourceName}
          />
        )}
        {activeTab === 'history' && (
          <HistoryTab
            jobHistory={jobHistory || []}
            retryingJobs={retryingJobs}
            stoppingJobs={stoppingJobs}
            handleViewJobLogs={handleViewJobLogs}
            handleRetryFailedJob={handleRetryFailedJob}
            handleStopJob={handleStopJob}
          />
        )}
        {activeTab === 'district-maps' && <DistrictMapsImportTab />}
      </div>

      {/* Job Log Viewer Modal */}
      {selectedJobIdForLogs && (
        <JobLogViewer
          jobId={selectedJobIdForLogs}
          isOpen={true}
          onClose={() => setSelectedJobIdForLogs(null)}
        />
      )}

      {/* Calendar Trigger Modal */}
      <CalendarTriggerModal
        isOpen={showCalendarTriggerModal}
        onClose={() => setShowCalendarTriggerModal(false)}
        onTrigger={handleCalendarTrigger}
        sourceName={calendarSourceInfo?.name || 'Colorado Calendar'}
      />
    </div>
  );
};

export default DataImportPage;
